import { CSSProperties } from 'react';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onSaveAndExit: () => void;
  onSaveAsDraft: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  hasValidationErrors?: boolean;
  validationErrorMessage?: string;
}

const UnsavedChangesModal = ({
  isOpen,
  onSaveAndExit,
  onSaveAsDraft,
  onDiscard,
  onCancel,
  hasValidationErrors = false,
  validationErrorMessage = ''
}: UnsavedChangesModalProps) => {
  if (!isOpen) return null;

  const overlayStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  };

  const modalStyle: CSSProperties = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
    borderRadius: '24px',
    padding: '2.5rem',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '2px solid rgba(102, 126, 234, 0.2)',
    animation: 'slideIn 0.3s ease',
    position: 'relative'
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem'
  };

  const iconStyle: CSSProperties = {
    fontSize: '2.5rem',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    borderRadius: '50%',
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)'
  };

  const titleStyle: CSSProperties = {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent'
  };

  const messageStyle: CSSProperties = {
    color: '#374151',
    fontSize: '1rem',
    lineHeight: '1.6',
    marginBottom: '2rem'
  };

  const buttonsContainerStyle: CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
    flexDirection: 'column'
  };

  const primaryButtonStyle: CSSProperties = {
    padding: '1rem 1.5rem',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem'
  };

  const draftButtonStyle: CSSProperties = {
    ...primaryButtonStyle,
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)'
  };

  const discardButtonStyle: CSSProperties = {
    ...primaryButtonStyle,
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
  };

  const cancelButtonStyle: CSSProperties = {
    padding: '1rem 1.5rem',
    background: 'white',
    color: '#64748b',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  return (
    <>
      <div style={overlayStyle} onClick={onCancel}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <div style={iconStyle}>⚠️</div>
            <h2 style={titleStyle}>Unsaved Changes</h2>
          </div>

          <p style={messageStyle}>
            You have unsaved changes to this question set. What would you like to do?
          </p>

          {hasValidationErrors && validationErrorMessage && (
            <div style={{
              padding: '1rem',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '2px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>⚠️</span>
              <div>
                <p style={{
                  margin: '0 0 0.5rem',
                  fontWeight: '600',
                  color: '#dc2626',
                  fontSize: '0.9rem'
                }}>
                  Cannot Publish - Missing Required Fields
                </p>
                <p style={{
                  margin: 0,
                  color: '#374151',
                  fontSize: '0.85rem',
                  lineHeight: '1.4'
                }}>
                  {validationErrorMessage}
                </p>
                <p style={{
                  margin: '0.5rem 0 0',
                  color: '#64748b',
                  fontSize: '0.8rem',
                  fontStyle: 'italic'
                }}>
                  Please complete all questions or save as draft.
                </p>
              </div>
            </div>
          )}

          <div style={buttonsContainerStyle}>
            <button
              style={{
                ...primaryButtonStyle,
                opacity: hasValidationErrors ? 0.5 : 1,
                cursor: hasValidationErrors ? 'not-allowed' : 'pointer',
                background: hasValidationErrors
                  ? '#d1d5db'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              }}
              onClick={onSaveAndExit}
              disabled={hasValidationErrors}
              onMouseEnter={(e) => {
                if (!hasValidationErrors) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!hasValidationErrors) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(16, 185, 129, 0.3)';
                }
              }}
            >
              <span>✅</span> Save & Publish Question Set
            </button>

            <button
              style={draftButtonStyle}
              onClick={onSaveAsDraft}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(245, 158, 11, 0.3)';
              }}
            >
              <span>📝</span> Save as Draft
            </button>

            <button
              style={discardButtonStyle}
              onClick={onDiscard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.3)';
              }}
            >
              <span>🗑️</span> Discard Changes
            </button>

            <button
              style={cancelButtonStyle}
              onClick={onCancel}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.color = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </>
  );
};

export default UnsavedChangesModal;
